/**
 * /admin/invitations — cross-org invitations directory (#1024 Slice 3
 * of #1021).
 *
 * Mirrors the `/admin/admins` shape from #1022 Slice 1: a directory
 * table with filter controls that reflect into URL params, no per-row
 * actions (revoke / resend deferred to a follow-up — the brief
 * explicitly ships v1 read-only). Each row shows invitee email,
 * inviter email (or a fallback when the inviter User row is
 * unresolvable), inviting org name, a status pill, the invitation
 * timestamp, and the expiry timestamp.
 *
 * URL params reflect filter state so an investigator can deep-link a
 * filtered view to a teammate ("look at all invitations from
 * @evil.com" → `/admin/invitations?inviteeSearch=evil.com`). The org
 * dropdown is populated from `adminOrgsAPI.list` — a small extra round-
 * trip on mount, but the org list is bounded and already cached on
 * the host (the Orgs admin page primes it on every prior visit).
 *
 * The `<RequireSuperadmin>` wrapper around `/admin/*` is UX defense-
 * in-depth; the actual data boundary is the server's `requireSession
 * + requireSuperadmin` chain on `/api/admin/invitations`, which is
 * also audited on every read.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  ADMIN_INVITATION_STATUSES,
  adminInvitationsAPI,
  adminOrgsAPI,
  type AdminInvitationRow,
  type AdminInvitationStatus,
  type AdminInvitationsFilter,
  type AdminOrgRow,
} from '@/lib/api'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatLocalDateTime } from '@/lib/utils'

/** Cast a URL-param string back into the AdminInvitationStatus union
 * (or undefined if the value isn't one of the known statuses). Keeps
 * a fat-finger URL from coercing into a typed-but-invalid filter. */
function parseStatus(s: string | null): AdminInvitationStatus | undefined {
  if (!s) return undefined
  return (ADMIN_INVITATION_STATUSES as readonly string[]).includes(s)
    ? (s as AdminInvitationStatus)
    : undefined
}

/** Map a synthetic status to a Badge variant. Pending is neutral
 * (warning hint of "still open"); accepted is success; expired /
 * canceled / rejected are all "done, not actionable" — surfaced
 * differently so an operator scanning a long list can spot the
 * patterns quickly. */
function statusVariant(s: AdminInvitationStatus): 'default' | 'success' | 'secondary' | 'warning' | 'destructive' {
  switch (s) {
    case 'pending':
      return 'warning'
    case 'accepted':
      return 'success'
    case 'expired':
      return 'secondary'
    case 'canceled':
      return 'destructive'
    case 'rejected':
      return 'destructive'
  }
}

/** Render an inviter cell. Falls back to a friendly placeholder when
 * the inviter User row was hard-deleted (`inviterEmail` null but
 * `inviterUserId` present) or when the row pre-dates inviterId
 * tracking (both null). */
function renderInviter(row: AdminInvitationRow): ReactElement {
  if (row.inviterEmail) return <span>{row.inviterEmail}</span>
  if (row.inviterUserId) {
    return (
      <span className="text-muted-foreground italic">
        (deleted user: {row.inviterUserId})
      </span>
    )
  }
  return <span className="text-muted-foreground italic">(unknown)</span>
}

export function InvitationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tz = useUserTimezone()

  // Filter state is sourced FROM the URL params on every render so
  // browser-history navigation (back/forward) restores the filtered
  // view automatically. Mutations write back to the URL via
  // `setSearchParams`.
  const status = parseStatus(searchParams.get('status'))
  const orgId = searchParams.get('orgId') ?? ''
  const inviteeSearch = searchParams.get('inviteeSearch') ?? ''
  const inviterSearch = searchParams.get('inviterSearch') ?? ''

  const [rows, setRows] = useState<AdminInvitationRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [orgs, setOrgs] = useState<AdminOrgRow[]>([])

  // Local input state for the two search fields so the URL doesn't
  // rewrite on every keystroke (debounced through the form-submit /
  // blur path). Kept in sync when the URL changes externally.
  const [inviteeInput, setInviteeInput] = useState(inviteeSearch)
  const [inviterInput, setInviterInput] = useState(inviterSearch)
  useEffect(() => setInviteeInput(inviteeSearch), [inviteeSearch])
  useEffect(() => setInviterInput(inviterSearch), [inviterSearch])

  // Load orgs once for the filter dropdown. The list is small and
  // already cached on every other admin tab, so a single mount fetch
  // suffices — no listener for new-org-created events.
  useEffect(() => {
    let cancelled = false
    void adminOrgsAPI.list().then(
      (r) => {
        if (!cancelled) setOrgs(r)
      },
      () => {
        // Org dropdown failure is non-fatal — the user can still
        // filter by status / email. Surface a console.warn but don't
        // bubble into the page-level error state.
        // eslint-disable-next-line no-console
        console.warn('[admin/invitations] failed to load orgs for filter dropdown')
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  // Reload invitations whenever any filter param changes. Each filter
  // primitive becomes a dependency so React re-runs the effect on a
  // URL-driven change OR an external `setSearchParams`.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const filter: AdminInvitationsFilter = {
      status,
      orgId: orgId || undefined,
      inviteeSearch: inviteeSearch || undefined,
      inviterSearch: inviterSearch || undefined,
    }
    void adminInvitationsAPI.list(filter).then(
      (r) => {
        if (cancelled) return
        setRows(r.invitations)
        setLoadError(null)
        setLoading(false)
      },
      (e) => {
        if (cancelled) return
        setLoadError(e instanceof Error ? e.message : 'Failed to load invitations')
        setLoading(false)
      },
    )
    return () => {
      cancelled = true
    }
  }, [status, orgId, inviteeSearch, inviterSearch])

  function updateParams(patch: Record<string, string | undefined>): void {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setSearchParams(next, { replace: true })
  }

  const statusOptions = useMemo(
    () => [
      { value: '', label: 'All statuses' },
      ...ADMIN_INVITATION_STATUSES.map((s) => ({
        value: s,
        // Capitalize for display — the wire value stays lowercase.
        label: s[0]!.toUpperCase() + s.slice(1),
      })),
    ],
    [],
  )

  const orgOptions = useMemo(
    () => [
      { value: '', label: 'All organizations' },
      ...orgs.map((o) => ({ value: o.id, label: o.name })),
    ],
    [orgs],
  )

  return (
    <div className="space-y-6 max-w-full">
      <div>
        <h1 className="text-2xl font-semibold">Invitations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every pending, accepted, and expired invitation across every
          organization on OttoTester. Audited on read. Read-only in v1
          — revoke and resend remain on the per-organization settings
          page.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label htmlFor="filter-status">Status</Label>
              <Select
                id="filter-status"
                value={status ?? ''}
                onChange={(e) =>
                  updateParams({ status: e.target.value || undefined })
                }
                options={statusOptions}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-org">Organization</Label>
              <Select
                id="filter-org"
                value={orgId}
                onChange={(e) =>
                  updateParams({ orgId: e.target.value || undefined })
                }
                options={orgOptions}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-invitee">Invitee email contains</Label>
              <Input
                id="filter-invitee"
                value={inviteeInput}
                onChange={(e) => setInviteeInput(e.target.value)}
                onBlur={() => updateParams({ inviteeSearch: inviteeInput || undefined })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    updateParams({ inviteeSearch: inviteeInput || undefined })
                  }
                }}
                placeholder="e.g. @evil.com"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-inviter">Inviter email contains</Label>
              <Input
                id="filter-inviter"
                value={inviterInput}
                onChange={(e) => setInviterInput(e.target.value)}
                onBlur={() => updateParams({ inviterSearch: inviterInput || undefined })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    updateParams({ inviterSearch: inviterInput || undefined })
                  }
                }}
                placeholder="e.g. alice"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {loadError && (
        <p className="text-sm text-destructive" role="alert">
          {loadError}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Invitations ({loading && !rows ? '…' : rows?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invitee</TableHead>
                  <TableHead>Inviter</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invited at</TableHead>
                  <TableHead>Expires at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && !rows && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-sm text-muted-foreground py-8"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {rows?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.inviteeEmail}</TableCell>
                    <TableCell className="text-sm">{renderInviter(row)}</TableCell>
                    <TableCell className="text-sm">
                      {row.organizationName ?? (
                        <span className="text-muted-foreground italic">
                          (deleted org: {row.organizationId})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(row.status)}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.invitedAt ? formatLocalDateTime(row.invitedAt, tz) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.expiresAt ? formatLocalDateTime(row.expiresAt, tz) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {rows && rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-sm text-muted-foreground py-8"
                    >
                      No invitations match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InvitationsPage
